/**
 * TestingSessionsPage
 *
 * Admin-only page for managing testing sessions.
 * Simple list view of testing sessions for AI detection validation.
 */

import React, { useEffect, useState, useCallback } from 'react'
import { useNavigate } from 'react-router-dom'
import {
  Plus,
  Trash2,
  FlaskConical,
  Images,
  Calendar,
  User,
  MoreVertical,
  X,
} from 'lucide-react'
import { Session, TestingSessionCreate } from '@/types'
import { apiService } from '@/services/api'
import { cn } from '@/utils/cn'

interface CreateSessionModalProps {
  isOpen: boolean
  onClose: () => void
  onSubmit: (data: TestingSessionCreate) => Promise<void>
  isSubmitting: boolean
}

const CreateSessionModal: React.FC<CreateSessionModalProps> = ({
  isOpen,
  onClose,
  onSubmit,
  isSubmitting,
}) => {
  const [name, setName] = useState('')
  const [description, setDescription] = useState('')

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    if (!name.trim()) return
    await onSubmit({ name: name.trim(), description: description.trim() })
    setName('')
    setDescription('')
  }

  if (!isOpen) return null

  return (
    <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
      <div className="bg-white rounded-xl shadow-xl max-w-md w-full">
        <div className="flex items-center justify-between p-4 border-b">
          <h2 className="text-lg font-semibold text-gray-900">New Testing Session</h2>
          <button onClick={onClose} className="p-1 hover:bg-gray-100 rounded-lg">
            <X className="h-5 w-5 text-gray-500" />
          </button>
        </div>
        <form onSubmit={handleSubmit} className="p-4 space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Session Name <span className="text-red-500">*</span>
            </label>
            <input
              type="text"
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="e.g., Batch Test Feb 10"
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
              autoFocus
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Description
            </label>
            <textarea
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              placeholder="Optional notes about this testing session..."
              rows={3}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500 resize-none"
            />
          </div>
          <div className="flex justify-end gap-3 pt-2">
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2 text-gray-700 hover:bg-gray-100 rounded-lg transition-colors"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={!name.trim() || isSubmitting}
              className={cn(
                'px-4 py-2 rounded-lg font-medium transition-colors',
                name.trim() && !isSubmitting
                  ? 'bg-primary-600 text-white hover:bg-primary-700'
                  : 'bg-gray-200 text-gray-500 cursor-not-allowed'
              )}
            >
              {isSubmitting ? 'Creating...' : 'Create Session'}
            </button>
          </div>
        </form>
      </div>
    </div>
  )
}

const TestingSessionsPage: React.FC = () => {
  const navigate = useNavigate()

  const [sessions, setSessions] = useState<Session[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState('')
  const [showCreateModal, setShowCreateModal] = useState(false)
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [contextMenuSession, setContextMenuSession] = useState<{
    session: Session
    x: number
    y: number
  } | null>(null)

  const fetchSessions = useCallback(async () => {
    try {
      setLoading(true)
      const response = await apiService.getTestingSessions()
      setSessions(response.sessions)
    } catch (err: any) {
      console.error('Failed to fetch testing sessions:', err)
      setError('Failed to load testing sessions')
    } finally {
      setLoading(false)
    }
  }, [])

  useEffect(() => {
    fetchSessions()
  }, [fetchSessions])

  // Close context menu when clicking outside
  useEffect(() => {
    const handleClickOutside = () => setContextMenuSession(null)
    if (contextMenuSession) {
      document.addEventListener('click', handleClickOutside)
      return () => document.removeEventListener('click', handleClickOutside)
    }
  }, [contextMenuSession])

  const handleCreateSession = async (data: TestingSessionCreate) => {
    try {
      setIsSubmitting(true)
      const newSession = await apiService.createTestingSession(data)
      setShowCreateModal(false)
      navigate(`/testing/${newSession.id}`)
    } catch (err: any) {
      console.error('Failed to create testing session:', err)
      alert('Failed to create testing session')
    } finally {
      setIsSubmitting(false)
    }
  }

  const handleDeleteSession = async (sessionId: number) => {
    if (!confirm('Are you sure you want to delete this testing session? All analyses will be permanently deleted.')) {
      return
    }
    try {
      await apiService.deleteTestingSession(sessionId)
      setSessions((prev) => prev.filter((s) => s.id !== sessionId))
    } catch (err: any) {
      console.error('Failed to delete testing session:', err)
      alert('Failed to delete testing session')
    }
    setContextMenuSession(null)
  }

  const formatDate = (dateString: string) => {
    const date = new Date(dateString)
    return date.toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
    })
  }

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600"></div>
      </div>
    )
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-serif font-bold text-gray-900 flex items-center gap-2">
            <FlaskConical className="h-6 w-6 text-purple-600" />
            Testing Playground
          </h1>
          <p className="text-gray-600 mt-1">
            Test AI detection accuracy with batch image uploads
          </p>
        </div>
        <button
          onClick={() => setShowCreateModal(true)}
          className="flex items-center gap-2 px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 transition-colors font-medium"
        >
          <Plus className="h-4 w-4" />
          New Session
        </button>
      </div>

      {/* Error */}
      {error && (
        <div className="bg-red-50 border border-red-200 rounded-md p-4">
          <p className="text-red-700">{error}</p>
        </div>
      )}

      {/* Sessions List */}
      {sessions.length === 0 ? (
        <div className="bg-white rounded-xl border border-gray-200 p-12 text-center">
          <FlaskConical className="h-12 w-12 text-gray-400 mx-auto mb-4" />
          <h3 className="text-lg font-medium text-gray-900 mb-2">No testing sessions yet</h3>
          <p className="text-gray-600 mb-6">
            Create your first testing session to start validating AI detection accuracy.
          </p>
          <button
            onClick={() => setShowCreateModal(true)}
            className="inline-flex items-center gap-2 px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 transition-colors font-medium"
          >
            <Plus className="h-4 w-4" />
            Create Testing Session
          </button>
        </div>
      ) : (
        <div className="space-y-3">
          {sessions.map((session) => (
            <div
              key={session.id}
              onClick={() => navigate(`/testing/${session.id}`)}
              className="bg-white rounded-xl border border-gray-200 p-4 hover:border-primary-300 hover:shadow-md transition-all cursor-pointer"
            >
              <div className="flex items-center justify-between">
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-3">
                    <h3 className="text-lg font-semibold text-gray-900 truncate">
                      {session.name}
                    </h3>
                    {session.image_count !== undefined && session.image_count > 0 && (
                      <span className="inline-flex items-center gap-1 px-2 py-0.5 bg-purple-100 text-purple-700 rounded-full text-sm font-medium">
                        <Images className="h-3.5 w-3.5" />
                        {session.image_count} images
                      </span>
                    )}
                  </div>
                  {session.description && (
                    <p className="text-gray-600 text-sm mt-1 line-clamp-1">
                      {session.description}
                    </p>
                  )}
                  <div className="flex items-center gap-4 mt-2 text-xs text-gray-500">
                    <span className="flex items-center gap-1">
                      <Calendar className="h-3.5 w-3.5" />
                      {formatDate(session.created_at)}
                    </span>
                    {session.creator_username && (
                      <span className="flex items-center gap-1">
                        <User className="h-3.5 w-3.5" />
                        {session.creator_username}
                      </span>
                    )}
                  </div>
                </div>
                <button
                  onClick={(e) => {
                    e.stopPropagation()
                    setContextMenuSession({
                      session,
                      x: e.clientX,
                      y: e.clientY,
                    })
                  }}
                  className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
                >
                  <MoreVertical className="h-5 w-5 text-gray-500" />
                </button>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Context Menu */}
      {contextMenuSession && (
        <div
          className="fixed bg-white border border-gray-200 rounded-lg shadow-xl z-50 py-1 min-w-[160px]"
          style={{
            left: Math.min(contextMenuSession.x, window.innerWidth - 180),
            top: Math.min(contextMenuSession.y, window.innerHeight - 120),
          }}
          onClick={(e) => e.stopPropagation()}
        >
          <button
            onClick={() => {
              navigate(`/testing/${contextMenuSession.session.id}`)
              setContextMenuSession(null)
            }}
            className="w-full px-4 py-2 text-left text-sm text-gray-700 hover:bg-gray-100 flex items-center gap-2"
          >
            <FlaskConical className="h-4 w-4" />
            View Session
          </button>
          <div className="border-t border-gray-100 my-1" />
          <button
            onClick={() => handleDeleteSession(contextMenuSession.session.id)}
            className="w-full px-4 py-2 text-left text-sm text-red-600 hover:bg-red-50 flex items-center gap-2"
          >
            <Trash2 className="h-4 w-4" />
            Delete
          </button>
        </div>
      )}

      {/* Create Session Modal */}
      <CreateSessionModal
        isOpen={showCreateModal}
        onClose={() => setShowCreateModal(false)}
        onSubmit={handleCreateSession}
        isSubmitting={isSubmitting}
      />
    </div>
  )
}

export default TestingSessionsPage
